import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { baseUrl, InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  createTrustLedgerInterest,
  deleteTrustLedgerInterest,
  getTrustLedgerInterest,
  updateTrustLedgerInterest,
} from 'services'
import { ButtonGroup, FormTable } from 'stories/components'
import { getPrice3decimal, removeComma, solveDecimalJavascriptSum } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { interestDefaultInputs, interestHeader } from './constants'
import { IEscrowLedger } from './types'

const tabOptions: Record<string, string> = {
  draw: 'Draw disbursement Ledger',
  escrow: 'Escrow Ledger',
  interest: 'Interest Reserve Ledger',
}

export const EscrowLedger = () => {
  const [loading, setLoading] = useState(false)
  const [tabType, setTabType] = useState(Object.keys(tabOptions)[0])
  const [initData, setInitData] = useState<Record<string, any>>({})
  const [interestData, setInterestData] = useState<Array<IEscrowLedger>>([])

  const token = useSelector((state: any) => state.auth.token)
  const loanNumber = setLoanNumber()

  const init = () => {
    setLoading(true)
    getTrustLedgerInterest()
      .then((data) => {
        setInitData(data)
        setInterestData(data.interestData)
      })
      .finally(() => setLoading(false))
  }

  const { interestReserve = -1 } = initData

  useEffect(() => {
    setLoading(true)
    init()
  }, [])

  const interestDataWithBalance = useMemo(() => {
    let balance = interestReserve === -1 ? 0 : interestReserve
    const newData = interestData
      .filter((item) => (tabType == 'interest' && !item.type) || item.type == tabType)
      .map((item) => {
        balance = solveDecimalJavascriptSum([
          removeComma(balance),
          -1 * removeComma(item.payment),
          removeComma(item.deposit),
        ])
        item.balance = getPrice3decimal(balance)
        return item
      })
    return newData
  }, [interestData, interestReserve, tabType])

  const onInterestSubmit = (currentId: any, values: Record<string, any>) => {
    return new Promise((resolve) => {
      setLoading(true)
      if (!currentId) {
        values = {
          id: Date.now(),
          type: tabType,
          ...values,
        }
        createTrustLedgerInterest(values)
          .then(() => {
            resolve(values)

            let temp = cloneDeep(interestData)
            temp.push(values as any)
            setInterestData(temp)
          })
          .finally(() => setLoading(false))
      } else {
        updateTrustLedgerInterest(currentId, values)
          .then(() => resolve(values))
          .finally(() => {
            let temp = cloneDeep(interestData)
            temp.map((item, index) => {
              if (item.id === currentId) temp[index] = values as any
            })
            setInterestData(temp)
            setLoading(false)
          })
      }
    })
  }

  const onInterestRemove = async (id: any) => {
    return new Promise((resolve) => {
      setLoading(true)
      deleteTrustLedgerInterest(id)
        .then(() => {
          resolve(true)
        })
        .finally(() => {
          let temp: any = []
          interestData.map((item) => {
            if (item.id !== id) temp.push(item)
          })
          setInterestData(temp)
          setLoading(false)
        })
    })
  }

  const onInterestExportExcel = () => {
    const url = `${baseUrl}/loan/downloadTrustLedgerInterest/${loanNumber}?token=${token}&type=${tabType}`

    var windowReference: any = window.open()
    windowReference.document.title = 'Downloading...'
    windowReference.location = url
  }

  const interestInputLogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    if (key === 'payment') {
      inputs['deposit'].value = 0
    }
    if (key === 'deposit') {
      inputs['payment'].value = 0
    }
    return inputs
  }

  return (
    <div>
      <LayoutLoading show={loading} />

      <ButtonGroup title={tabOptions} onChange={setTabType} value={tabType} />

      <div className="flex justify-between items-center mt-4 flex-wrap">
        <div className="font-variation-settings-600 text-lg">- {tabOptions[tabType]}</div>
        <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={onInterestExportExcel}>
          <div className="flex items-center">
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" /> Export to Excel
          </div>
        </button>
      </div>
      <FormTable
        header={interestHeader}
        addText={'Add New Item'}
        inputs={interestDefaultInputs}
        data={interestDataWithBalance}
        inputLogic={interestInputLogic}
        cols={3}
        onSubmit={onInterestSubmit}
        onRemove={onInterestRemove}
      />
    </div>
  )
}
